import { Component, OnInit } from '@angular/core';
import { MessageService } from 'primeng/api';
import { HttpService } from 'src/app/services/http.service';
import { resolveApiErrorMessage } from 'src/app/utils/api-error-message.util';
import { environment } from 'src/environments/environment';

interface DeliveryRatePolicy {
  policyVersion: number;
  firstKmMin: number;
  firstKmDefault: number;
  firstKmMax: number;
  additionalKmMin: number;
  additionalKmDefault: number;
  additionalKmMax: number;
  updatedAt?: string;
  updatedBy?: string;
  changeReason?: string;
  source?: string;
}

/**
 * Global delivery rate rails editor (Santiago/DR launch controls).
 * Mission TINYALL-DN-GLOBAL-RATE-RAILS-CHECKOUT-TOGGLE-ADMIN-EXECUTION-20260710.
 */
@Component({
  selector: 'app-delivery-rate-policy',
  templateUrl: './delivery-rate-policy.component.html',
  styleUrls: ['./delivery-rate-policy.component.scss'],
})
export class DeliveryRatePolicyComponent implements OnInit {
  private readonly policyUrl = `${environment.apiUrl}/admin-center/delivery-network/v1/rate-policy`;

  isLoading = false;
  isSaving = false;

  policy: DeliveryRatePolicy | null = null;

  firstKmMin: number | null = null;
  firstKmDefault: number | null = null;
  firstKmMax: number | null = null;
  additionalKmMin: number | null = null;
  additionalKmDefault: number | null = null;
  additionalKmMax: number | null = null;
  changeReason = '';

  constructor(
    private httpService: HttpService,
    private messageService: MessageService
  ) {}

  ngOnInit(): void {
    this.loadPolicy();
  }

  async loadPolicy() {
    this.isLoading = true;
    try {
      const policy: DeliveryRatePolicy = await this.httpService
        .doGet(this.policyUrl)
        .toPromise();
      this.applyPolicy(policy);
    } catch (error: any) {
      this.messageService.add({
        severity: 'error',
        summary: 'Error',
        detail: resolveApiErrorMessage(error, 'Could not load the delivery rate policy'),
      });
    } finally {
      this.isLoading = false;
    }
  }

  private applyPolicy(policy: DeliveryRatePolicy) {
    this.policy = policy;
    this.firstKmMin = policy.firstKmMin;
    this.firstKmDefault = policy.firstKmDefault;
    this.firstKmMax = policy.firstKmMax;
    this.additionalKmMin = policy.additionalKmMin;
    this.additionalKmDefault = policy.additionalKmDefault;
    this.additionalKmMax = policy.additionalKmMax;
    this.changeReason = '';
  }

  get validationError(): string | null {
    const values = [
      this.firstKmMin,
      this.firstKmDefault,
      this.firstKmMax,
      this.additionalKmMin,
      this.additionalKmDefault,
      this.additionalKmMax,
    ];
    if (values.some((value) => value === null || value === undefined || Number.isNaN(Number(value)))) {
      return 'All six rate values are required.';
    }
    if (Number(this.firstKmMin) <= 0) {
      return 'First km minimum must be greater than 0.';
    }
    if (Number(this.additionalKmMin) < 0) {
      return 'Additional km minimum cannot be negative.';
    }
    if (Number(this.firstKmMin) > Number(this.firstKmDefault) || Number(this.firstKmDefault) > Number(this.firstKmMax)) {
      return 'First km values must satisfy: minimum ≤ recommended ≤ maximum.';
    }
    if (
      Number(this.additionalKmMin) > Number(this.additionalKmDefault) ||
      Number(this.additionalKmDefault) > Number(this.additionalKmMax)
    ) {
      return 'Additional km values must satisfy: minimum ≤ recommended ≤ maximum.';
    }
    return null;
  }

  async save() {
    const error = this.validationError;
    if (error) {
      this.messageService.add({ severity: 'warn', summary: 'Invalid values', detail: error });
      return;
    }
    this.isSaving = true;
    const payload = {
      firstKmMin: Number(this.firstKmMin),
      firstKmDefault: Number(this.firstKmDefault),
      firstKmMax: Number(this.firstKmMax),
      additionalKmMin: Number(this.additionalKmMin),
      additionalKmDefault: Number(this.additionalKmDefault),
      additionalKmMax: Number(this.additionalKmMax),
      changeReason: this.changeReason?.trim() || null,
    };
    try {
      const policy: DeliveryRatePolicy = await this.httpService
        .doPatch(this.policyUrl, payload)
        .toPromise();
      this.applyPolicy(policy);
      this.messageService.add({
        severity: 'success',
        summary: 'Successful',
        detail: `Delivery rate policy saved (version ${policy.policyVersion})`,
        life: 3000,
      });
    } catch (error: any) {
      this.messageService.add({
        severity: 'error',
        summary: 'Error',
        detail: resolveApiErrorMessage(error, 'Could not save the delivery rate policy'),
      });
    } finally {
      this.isSaving = false;
    }
  }
}
